// src/utils/versionWatch.js
// Pure version-watch logic for deploy safety (DSAF-01, DSAF-02). No React, no
// firebase, no clock reads: callers pass `now` so every rule is testable.
//
// Build identity is the epoch `builtAt` (Date.parse of buildInfo.generatedAt),
// never the machine-local `build` stamp, which is not monotonic.
//
// Any inequality between the live build and the local build counts as an
// update, so a hosting rollback also pulls tabs onto the code now being served.
// A rollback past a forced minimum needs the minimum cleared first, otherwise
// the older build would still sit below it and be forced again.

export const OVERLAY_AFTER_MS = 30 * 60 * 1000;
export const AUTO_RELOAD_SECONDS = 10;
export const POLL_INTERVAL_MS = 4 * 60 * 1000;
export const MIN_FETCH_GAP_MS = 30 * 1000;
export const AUTO_EXPAND_AT_MS = 5 * 60 * 1000;

export const RELOAD_LOOP_MAX = 2;
export const RELOAD_LOOP_WINDOW_MS = 3 * 60 * 1000;
export const RELOADS_KEY = 'kf:reloads';

export function localBuildFrom(buildInfo) {
    return Date.parse(buildInfo && buildInfo.generatedAt);
}

export function parseVersionManifest(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    if (typeof value.version !== 'string') return null;
    if (typeof value.builtAt !== 'number' || !Number.isFinite(value.builtAt)) return null;
    const manifest = {
        version: value.version,
        builtAt: value.builtAt,
        commitHash: typeof value.commitHash === 'string' ? value.commitHash : '',
    };
    // publishedAt (epoch ms) is stamped by the deploy wrapper at go-live. Older
    // manifests lack it; anything that is not a positive finite number is ignored.
    if (typeof value.publishedAt === 'number' && Number.isFinite(value.publishedAt) && value.publishedAt > 0) {
        manifest.publishedAt = value.publishedAt;
    }
    return manifest;
}

export function computeSkewMs(dateHeader, clientNow) {
    if (!dateHeader) return 0;
    const serverMs = Date.parse(dateHeader);
    if (!Number.isFinite(serverMs)) return 0;
    return serverMs - clientNow;
}

export function firstSeenKey(builtAt) {
    return `kf:vw:firstSeen:${builtAt}`;
}

export function pillMinutes(remainingMs) {
    return Math.max(1, Math.ceil(remainingMs / 60000));
}

const hasMin = (minBuild) => typeof minBuild === 'number' && Number.isFinite(minBuild) && minBuild > 0;

// appConfig/clientVersion.minBuild, read with the server gate's strict rule: only a
// positive finite number counts. A numeric string from a Console edit is ignored
// on both sides, so tabs are never forced while the server lets every call through.
export function parseMinBuild(data) {
    const value = data && typeof data === 'object' ? data.minBuild : undefined;
    return hasMin(value) ? value : 0;
}

// The 30-minute clock (D-03) runs from go-live. builtAt stays the build identity
// for every comparison; publishedAt only moves the anchor, and only when it is a
// real past time. A manifest without it (older deploys) falls back to builtAt,
// which trails go-live by the build and upload time.
export function evaluate({ localBuild, remote, minBuild, firstDetectedAt, now }) {
    // An unparsable local build cannot be compared with anything; treating every
    // manifest as an update would walk the tab into the reload-loop stall.
    if (!Number.isFinite(localBuild)) {
        return { status: 'current', deadline: null };
    }
    if (hasMin(minBuild) && localBuild < minBuild) {
        // Lockout: the live build is itself below the minimum (a hosting rollback,
        // or a build machine whose clock runs behind). A reload would only load the
        // same refused code, so the tab waits for a fixed live build instead.
        if (remote && remote.builtAt < minBuild) return { status: 'lockout', deadline: null };
        return { status: 'forced', deadline: now };
    }
    if (!remote || remote.builtAt === localBuild) {
        return { status: 'current', deadline: null };
    }
    // This tab meets the minimum but the live build does not: never pull a working
    // tab onto code the server refuses.
    if (hasMin(minBuild) && remote.builtAt < minBuild) {
        return { status: 'current', deadline: null };
    }
    const firstSeen = typeof firstDetectedAt === 'number' ? firstDetectedAt : now;
    const published = typeof remote.publishedAt === 'number' && Number.isFinite(remote.publishedAt)
        && remote.publishedAt > 0 && remote.publishedAt <= now
        ? remote.publishedAt
        : null;
    let anchor;
    if (remote.builtAt < localBuild) {
        // Rollback (older remote): count from first detection. A rolled-back
        // release keeps its old publishedAt, which would skip the banner.
        anchor = firstSeen;
    } else if (published !== null) {
        anchor = published;
    } else if (remote.builtAt <= now) {
        anchor = remote.builtAt;
    } else {
        // A builtAt in the future (clock skew on the build machine): count from first detection.
        anchor = firstSeen;
    }
    if (anchor > now) anchor = now;
    const deadline = anchor + OVERLAY_AFTER_MS;
    return { status: now >= deadline ? 'overlay' : 'banner', deadline };
}

function readReloads(storage) {
    try {
        const raw = storage.getItem(RELOADS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && Number.isFinite(parsed.count) && Number.isFinite(parsed.firstAt)) return parsed;
    } catch {
        // fall through
    }
    return null;
}

export function noteAutoReload(storage, now) {
    const prev = readReloads(storage);
    const next =
        prev && now - prev.firstAt <= RELOAD_LOOP_WINDOW_MS
            ? { count: prev.count + 1, firstAt: prev.firstAt }
            : { count: 1, firstAt: now };
    try {
        storage.setItem(RELOADS_KEY, JSON.stringify(next));
    } catch {
        // storage unavailable; the guard simply cannot count
    }
    return next;
}

export function reloadLoopTripped(storage, now) {
    const prev = readReloads(storage);
    if (!prev) return false;
    return now - prev.firstAt <= RELOAD_LOOP_WINDOW_MS && prev.count > RELOAD_LOOP_MAX;
}
